import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, Mail, MapPin, Instagram, Facebook, Youtube, MessageSquare } from 'lucide-react';

// TikTok icon (not in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

export const Footer = () => {
  const { t, isRTL } = useLanguage();

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/drbooking.pro', color: 'hover:text-pink-500' },
    { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/drbooking.pro', color: 'hover:text-blue-500' },
    { name: 'TikTok', icon: TikTokIcon, url: 'https://tiktok.com/@drbooking.pro', color: 'hover:text-white' },
    { name: 'YouTube', icon: Youtube, url: 'https://youtube.com/@drbooking.pro', color: 'hover:text-red-500' },
    { name: 'WhatsApp', icon: MessageSquare, url: 'https://wa.me/352621411475', color: 'hover:text-green-500' },
  ];

  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className={`grid grid-cols-1 md:grid-cols-4 gap-8 ${isRTL ? 'md:grid-flow-col-dense' : ''}`}>
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white">drbooking</span>
            </Link>
            <p className="text-sm text-slate-400">
              {t('hero_subtitle').substring(0, 100)}...
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('nav_home')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/professionals" className="text-sm hover:text-primary transition-colors">
                  {t('nav_professionals')}
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-sm hover:text-primary transition-colors">
                  {t('nav_pricing')}
                </Link>
              </li>
              <li>
                <Link to="/register" className="text-sm hover:text-primary transition-colors">
                  {t('nav_register')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">Légal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/cgu" className="text-sm hover:text-primary transition-colors">
                  CGU
                </Link>
              </li>
              <li>
                <Link to="/mentions-legales" className="text-sm hover:text-primary transition-colors">
                  Mentions légales
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-sm hover:text-primary transition-colors">
                  Politique de confidentialité
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-white mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-primary" />
                <a href="mailto:info@drbooking.pro" className="hover:text-primary">info@drbooking.pro</a>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-primary" />
                <span>Luxembourg</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Social Links */}
        <div className="border-t border-slate-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-400">Suivez-nous :</span>
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center transition-all ${social.color}`}
                    title={social.name}
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
            <p className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} drbooking. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
